import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

export class ProductShowcase extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.cards = this.querySelectorAll('[data-showcase-card]');
    this.image = this.querySelector('[ref="showcaseImage"]');
    this.titleEl = this.querySelector('[ref="showcaseTitle"]');
    this.price = this.querySelector('[ref="showcasePrice"]');
    this.comparePrice = this.querySelector('[ref="showcaseComparePrice"]');
    this.quickAddBtn = this.querySelector('[ref="showcaseQuickAdd"]');
    this.activeVariantId = this.querySelector('[ref="showcaseImage"]')?.dataset.variantId;
    this.activeIndex = 0;
    this.productCount = parseInt(this.dataset.productCount, 10) || 0;

    this.cards.forEach((card) => {
      card.addEventListener('click', this.#handleCardClick);
    });

    this.quickAddBtn?.addEventListener('click', this.#handleQuickAdd);

    this.#initScrollSwitch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.cards?.forEach((card) => {
      card.removeEventListener('click', this.#handleCardClick);
    });

    this.quickAddBtn?.removeEventListener('click', this.#handleQuickAdd);
    this.#destroyScrollSwitch();
  }

  #initScrollSwitch() {
    if (this.productCount < 2) return;

    this._boundHandleScroll = this.#handleScroll.bind(this);
    window.addEventListener('scroll', this._boundHandleScroll, { passive: true });

    this._resizeHandler = () => {
      if (window.matchMedia('(max-width: 768px)').matches) {
        this.#destroyScrollSwitch();
      }
    };
    window.addEventListener('resize', this._resizeHandler);
  }

  #destroyScrollSwitch() {
    if (this._boundHandleScroll) {
      window.removeEventListener('scroll', this._boundHandleScroll);
      this._boundHandleScroll = null;
    }
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }
  }

  #handleScroll() {
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight;
    const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));

    const segmentSize = 1 / this.productCount;
    let newIndex = Math.floor(progress / segmentSize);
    if (newIndex >= this.productCount) newIndex = this.productCount - 1;

    if (newIndex !== this.activeIndex) {
      this.#switchToCard(newIndex);
    }
  }

  #switchToCard(index) {
    const card = this.cards[index];
    if (!card) return;

    this.activeIndex = index;

    this.cards.forEach((c) => c.classList.remove('product-showcase__card--active'));
    card.classList.add('product-showcase__card--active');

    this.#updateProduct(card);
  }

  #updateProduct(card) {
    const imageUrl = card.dataset.imageUrl;
    const imageAlt = card.dataset.imageAlt || '';
    const title = card.dataset.title;
    const price = card.dataset.price;
    const compareAtPrice = card.dataset.compareAtPrice;
    const variantId = card.dataset.variantId;

    this.activeVariantId = variantId;

    if (this.image) {
      this.image.style.opacity = '0';
      setTimeout(() => {
        this.image.src = imageUrl;
        this.image.alt = imageAlt;
        this.image.dataset.variantId = variantId;
        this.image.style.opacity = '1';
      }, 250);
    }

    if (this.titleEl) {
      this.titleEl.textContent = title;
    }

    if (this.price) {
      this.price.textContent = price;
    }

    if (this.comparePrice) {
      if (compareAtPrice && compareAtPrice !== '' && compareAtPrice !== price) {
        this.comparePrice.textContent = compareAtPrice;
        this.comparePrice.style.display = '';
      } else {
        this.comparePrice.textContent = '';
        this.comparePrice.style.display = 'none';
      }
    }
  }

  #handleCardClick = (event) => {
    const card = event.currentTarget;
    const index = Array.from(this.cards).indexOf(card);

    this.cards.forEach((c) => c.classList.remove('product-showcase__card--active'));
    card.classList.add('product-showcase__card--active');

    this.activeIndex = index;
    this.#updateProduct(card);
  };

  #handleQuickAdd = async (event) => {
    event.preventDefault();

    const variantId = this.activeVariantId;
    if (!variantId) return;

    const btn = this.quickAddBtn;
    btn.disabled = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript'),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        }),
      });

      const data = await response.json();

      if (data.status) {
        console.error('Add to cart error:', data.message);
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'product-showcase',
          itemCount: 1,
          productId: btn.dataset.productId || '',
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('Add to cart error:', error);
    } finally {
      btn.disabled = false;
    }
  };
}

customElements.define('product-showcase', ProductShowcase);
